/**
 * Fan waitlist admin — waitlist-period-only tool.
 * Once config/platform.waitlistOpen flips to false, fan signup is instant and
 * this queue is unused (keep the screen; do not delete).
 *
 * Uses getDocs + poll (not onSnapshot) to avoid Firestore watch-target races
 * under React Strict Mode remounts on older/buggy SDK builds.
 */

import {
  collection,
  doc,
  getDocs,
  updateDoc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { approveFanWaitlistEntry } from "./adminApprovalService";

const POLL_MS = 15000;

function mapFans(snap) {
  return snap.docs.map((d) => ({
    id: d.id,
    email: d.data().email || d.id,
    ...d.data(),
  }));
}

export function subscribeFanWaitlist(onData, onError) {
  let cancelled = false;

  const load = async () => {
    try {
      const snap = await getDocs(collection(db, "waitlist_fans"));
      if (cancelled) return;
      onData(mapFans(snap));
    } catch (err) {
      console.error("Fan waitlist load error:", err);
      if (!cancelled) onError?.(err);
    }
  };

  load();
  const timer = setInterval(load, POLL_MS);

  let cleaned = false;
  return () => {
    if (cleaned) return;
    cleaned = true;
    cancelled = true;
    clearInterval(timer);
  };
}

export async function approveFan(email) {
  return approveFanWaitlistEntry(email);
}

/** Bulk approve — flips approved=true so onFanApproved fires per doc. */
export async function approveFansBulk(emails) {
  const list = [...new Set((emails || []).map((e) => String(e).trim().toLowerCase()).filter(Boolean))];
  if (!list.length) return 0;

  // Firestore batches max 500; chunk
  let count = 0;
  for (let i = 0; i < list.length; i += 400) {
    const chunk = list.slice(i, i + 400);
    const batch = writeBatch(db);
    chunk.forEach((email) => {
      batch.set(
        doc(db, "waitlist_fans", email),
        {
          approved: true,
          approvedAt: serverTimestamp(),
          role: "fan",
          isCreator: false,
          email,
        },
        { merge: true }
      );
    });
    await batch.commit();
    count += chunk.length;
  }
  return count;
}

export async function approveFanSingle(email) {
  const clean = email.trim().toLowerCase();
  await updateDoc(doc(db, "waitlist_fans", clean), {
    approved: true,
    approvedAt: serverTimestamp(),
    role: "fan",
    isCreator: false,
  });
}
